import React from 'react';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { object, number, func, oneOfType, node } from 'prop-types';
import { ifElse, has, identity, prop } from 'ramda';
import ReactMarkdown from 'react-markdown';
import { format, currency } from '@olxbr/mojo-jojo/utils/string';
import VisualHighlight from '../product/product-detail/VisualHighlight';
import VerifiedHighlight from '../product/product-detail/VerifiedHighlight';
import VisualTag from './product-detail/VisualTag';
import checkmark from '../../images/checkmark.png';
import discount from '../../images/discount.svg';
import SmallPrice from './SmallPrice';

const Item = styled.div`
  color: #4a4a4a;
  font-size: 14px;
  line-height: 1.3;
  margin: 20px 0 0 11px;
  position: relative;
  text-align: left;

  strong {
    font-weight: bold
  }
  del {
    color:#d2d2d2
  }
`;

const Container = styled.div`
  display: flex;
`;

const CheckMarkContainer = styled.div`
  width: 25px;
  > img {
    max-width: none;
  }
`;

const Badge = styled.img`
    margin: 20px 0 0 11px;
`;

const CheckmarkBadge = styled(Badge)`
  height: 13px;
  width: 15px;
`;

const DiscountBadge = styled(Badge)`
  margin-left: 9px;
  height: 19px;
  width: 21px;
`;

const HelpIcon = styled.img`
  margin-top: 23px;
  width: 12px;
  height: 12px;
  cursor: pointer;
`;

const getPreviousPrice = ifElse(
  has('previous'),
  prop('previous'),
  identity,
);

const Paragraph = ({ children }) => <p style={{ display: 'inline' }}>{children}</p>;

const Characteristic = ({
  characteristic, price, amount, openModal, referencePrice,
}) => {
  const currentPrice = getPreviousPrice(price);
  const adUnitPrice = currentPrice / amount;
  const discountPercentage = (((referencePrice - adUnitPrice) / referencePrice) * 100).toFixed(0);
  const defaultRenderer = { paragraph: Paragraph };
  const colorHighlightBadgeBoost = characteristic.link === 'highlightBadgeBoost' ? '#F28000' : '#6e0ad6';

  const renderers = characteristic.visualHighlight || characteristic.verifiedHighlight ? {
    ...defaultRenderer,
    strong: props => (characteristic.verifiedHighlight ? VerifiedHighlight : <VisualHighlight {...props} color={colorHighlightBadgeBoost}/>),
    emphasis: props => <VisualTag disabled={!characteristic.checkmark} help {...props} />,
  } : defaultRenderer;

  return (
    <Container>
      <CheckMarkContainer>
        {
          (characteristic.discount && <DiscountBadge src={discount} />)
          ||
          (characteristic.checkmark && <CheckmarkBadge src={checkmark} />)
        }
      </CheckMarkContainer>
      <Item>
        <ReactMarkdown
          renderers={renderers}
          source={format(characteristic.text, {
            pricePerAd: ReactDOMServer.renderToString(<SmallPrice value={adUnitPrice} />),
            referencePrice: currency(referencePrice),
            discountPercentage: `${discountPercentage}%`,
          })}
          escapeHtml={false}
        />
      </Item>
      {characteristic.olxpro &&
        <HelpIcon
          data-e2e="olxpro-modal-trigger"
          src={`${process.env.PUBLIC_URL}/images/help-circle.svg`}
          alt="olx pro detalhes"
          onClick={openModal}
        />
      }
    </Container>
  );
};

Characteristic.propTypes = {
  characteristic: object.isRequired,
  price: oneOfType([number, object]),
  amount: number,
  openModal: func,
  referencePrice: number,
};

Characteristic.defaultProps = {
  price: 0.00,
  referencePrice: 0.00,
  amount: 1,
  openModal: () => { },
};

Paragraph.propTypes = {
  children: node.isRequired,
};

export default Characteristic;
